import { useEffect, useRef, useState } from "hono/jsx";

interface InventorySlot {
  itemId: number | null;
  count: number;
  variation?: number;
}

interface InventoryProps {
  isOpen: boolean;
}

export function Inventory({ isOpen }: InventoryProps) {
  // 9 hotbar slots + 27 inventory slots = 36 total
  const [slots, _setSlots] = useState<InventorySlot[]>(
    Array(36).fill(null).map(() => ({ itemId: null, count: 0 })),
  );
  const [selectedSlot, setSelectedSlot] = useState(0);
  const [hotbarActive, setHotbarActive] = useState(false);
  const fadeTimeoutRef = {
    current: 0 as unknown as ReturnType<typeof setTimeout>,
  };

  // Cursor Item for Drag & Drop
  const [cursorItem, setCursorItem] = useState<InventorySlot | null>(null);
  const [mousePos, setMousePos] = useState({ x: 0, y: 0 });

  // Drag tracking
  const dragStartSlot = useRef<number | null>(null);

  // Show hotbar fully, then fade after delay
  const flashHotbar = () => {
    setHotbarActive(true);
    clearTimeout(fadeTimeoutRef.current);
    fadeTimeoutRef.current = setTimeout(() => setHotbarActive(false), 1500);
  };

  // Listen for mouse move (global) for moving cursor item
  useEffect(() => {
    const onMove = (e: MouseEvent) => {
      if (isOpen || cursorItem) setMousePos({ x: e.clientX, y: e.clientY });
    };
    const onVirtualMove = (e: CustomEvent) => {
      const { x, y } = e.detail;
      if (isOpen || cursorItem) setMousePos({ x, y });
    };

    window.addEventListener("mousemove", onMove);
    document.addEventListener(
      "vox:cursor:move",
      onVirtualMove as EventListener,
    );
    return () => {
      window.removeEventListener("mousemove", onMove);
      document.removeEventListener(
        "vox:cursor:move",
        onVirtualMove as EventListener,
      );
    };
  }, [isOpen, cursorItem]);

  // Dispatch update to voxhole.ts
  const updateGlobalState = (newSlots: InventorySlot[]) => {
    const hotbar = newSlots.slice(0, 9);
    const inventory = newSlots.slice(9, 36);
    document.dispatchEvent(
      new CustomEvent("vox:inventory:update", {
        detail: { inventory, hotbar },
      }),
    );
  };

  // Core Interaction Logic
  const performPickup = (index: number) => {
    const slot = slots[index];
    if (slot.itemId !== null && slot.count > 0) {
      setCursorItem(slot);
      const newSlots = [...slots];
      newSlots[index] = { itemId: null, count: 0 };
      _setSlots(newSlots);
      updateGlobalState(newSlots);
      return true;
    }
    return false;
  };

  const performPlace = (index: number) => {
    if (!cursorItem) return;

    const clickedSlot = slots[index];
    const newSlots = [...slots];
    const isSameItem = clickedSlot.itemId === cursorItem.itemId &&
      (clickedSlot.variation || 0) === (cursorItem.variation || 0);

    if (clickedSlot.itemId === null || clickedSlot.count <= 0) {
      // Place into empty slot
      newSlots[index] = cursorItem;
      setCursorItem(null);
      _setSlots(newSlots);
      updateGlobalState(newSlots);
    } else if (isSameItem) {
      // Stack
      const space = 64 - clickedSlot.count;
      if (space > 0) {
        const toAdd = Math.min(space, cursorItem.count);
        newSlots[index] = { ...clickedSlot, count: clickedSlot.count + toAdd };

        if (cursorItem.count > toAdd) {
          setCursorItem({ ...cursorItem, count: cursorItem.count - toAdd });
        } else {
          setCursorItem(null);
        }
        _setSlots(newSlots);
        updateGlobalState(newSlots);
      }
    } else {
      // Swap
      newSlots[index] = cursorItem;

      // Check if we can swap to original slot
      if (dragStartSlot.current !== null && dragStartSlot.current !== index) {
        // Put clicked item into source slot
        newSlots[dragStartSlot.current] = clickedSlot;
        setCursorItem(null);
      } else {
        // Fallback: Swap to Hand (if drag source unknown/same)
        setCursorItem(clickedSlot);
      }

      _setSlots(newSlots);
      updateGlobalState(newSlots);
    }
  };

  const performShiftClick = (index: number) => {
    const clickedSlot = slots[index];
    if (clickedSlot.itemId === null || clickedSlot.count <= 0) return;

    const newSlots = [...slots];
    let targetIndex = -1;

    if (index >= 9) {
      // Inventory -> Hotbar (0-8)
      for (let i = 0; i < 9; i++) {
        if (newSlots[i].itemId === null || newSlots[i].count <= 0) {
          targetIndex = i;
          break;
        }
      }
    } else {
      // Hotbar -> Inventory (9-35)
      for (let i = 9; i < 36; i++) {
        if (newSlots[i].itemId === null || newSlots[i].count <= 0) {
          targetIndex = i;
          break;
        }
      }
    }

    if (targetIndex !== -1) {
      newSlots[targetIndex] = clickedSlot;
      newSlots[index] = { itemId: null, count: 0 };
      _setSlots(newSlots);
      updateGlobalState(newSlots);
    }
  };

  const handleMouseDown = (index: number, e: MouseEvent) => {
    if (e.button !== 0) return; // Only Left Click
    if (e.shiftKey) {
      performShiftClick(index);
      return;
    }
    if (!isOpen && index >= 9) return; // Ignore invisible slots? Usually prevented by UI hiding

    flashHotbar();
    if (index < 9) {
      setSelectedSlot(index);
      document.dispatchEvent(
        new CustomEvent("vox:hotbar:select", { detail: index }),
      );
    }

    if (!cursorItem) {
      // Pickup
      const picked = performPickup(index);
      if (picked) {
        dragStartSlot.current = index;
      }
    } else {
      // Place / Swap
      performPlace(index);
      // If we just placed/swapped by CLICKING (Down), we end the "drag" context
      dragStartSlot.current = null;
    }
  };

  const handleMouseUp = (index: number) => {
    // Handle Drag & Drop Release
    // Only trigger if we are holding something AND we started dragging from a valid slot
    if (cursorItem && dragStartSlot.current !== null) {
      // If moved to different slot, treat as Drag Drop -> Place
      if (index !== dragStartSlot.current) {
        performPlace(index);
        // Reset drag start so subsequent Up events dont trigger
        dragStartSlot.current = null;
      }
      // If index === dragStartSlot.current, do nothing -> Treat as "Click to Hold"
    }
  };

  // Listen for hotbar selection (1-9 keys)
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      const num = parseInt(e.key);
      if (num >= 1 && num <= 9) {
        setSelectedSlot(num - 1);
        flashHotbar();
        document.dispatchEvent(
          new CustomEvent("vox:hotbar:select", { detail: num - 1 }),
        );
      }
    };

    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, []);

  // Listen for mouse wheel
  useEffect(() => {
    const handleWheel = (e: WheelEvent) => {
      if (isOpen) return;

      setSelectedSlot((prev) => {
        const next = e.deltaY > 0 ? (prev + 1) % 9 : (prev - 1 + 9) % 9;
        flashHotbar();
        document.dispatchEvent(
          new CustomEvent("vox:hotbar:select", { detail: next }),
        );
        return next;
      });
    };

    document.addEventListener("wheel", handleWheel);
    return () => document.removeEventListener("wheel", handleWheel);
  }, [isOpen]);

  // Listen for server inventory synchronization
  useEffect(() => {
    const handleInventorySet = (e: CustomEvent) => {
      const data = e.detail;
      const newSlots = new Array(36).fill(null).map(() => ({
        itemId: null,
        count: 0,
      }));

      if (data.hotbar) {
        // deno-lint-ignore no-explicit-any
        data.hotbar.forEach((slot: any, i: number) => {
          if (slot && i < 9) newSlots[i] = slot;
        });
      }
      if (data.inventory) {
        // deno-lint-ignore no-explicit-any
        data.inventory.forEach((slot: any, i: number) => {
          if (slot && i < 27) newSlots[i + 9] = slot;
        });
      }

      _setSlots(newSlots);
      if (data.selectedSlot !== undefined) {
        setSelectedSlot(data.selectedSlot);
        document.dispatchEvent(
          new CustomEvent("vox:hotbar:select", { detail: data.selectedSlot }),
        );
      }
    };

    document.addEventListener(
      "vox:inventory:set",
      handleInventorySet as EventListener,
    );
    return () =>
      document.removeEventListener(
        "vox:inventory:set",
        handleInventorySet as EventListener,
      );
  }, []);

  // Track hovered slot
  const [hoveredSlot, setHoveredSlot] = useState<number | null>(null);

  const hotbarVisible = isOpen || hotbarActive;

  return (
    <div
      className="fixed bottom-0 right-0 z-40 flex flex-col items-end"
      style={{ pointerEvents: isOpen ? "auto" : "none" }}
    >
      {/* Visual Cursor Item */}
      {cursorItem && (
        <div
          className="fixed w-10 h-10 pointer-events-none z-50 flex items-center justify-center bg-base-100/80 border border-primary rounded shadow-xl"
          style={{
            left: 0,
            top: 0,
            transform:
              `translate(${mousePos.x}px, ${mousePos.y}px) translate(-50%, -50%)`,
          }}
        >
          <span className="text-xl font-bold">{cursorItem.itemId}</span>
          <span className="absolute bottom-0 right-0.5 text-[10px] font-mono">
            {cursorItem.count}
          </span>
          {cursorItem.variation !== undefined && cursorItem.variation > 0 && (
            <span className="absolute top-0 right-0 text-[8px] text-accent">
              v{cursorItem.variation}
            </span>
          )}
        </div>
      )}

      {/* Main Inventory Grid */}
      {isOpen && (
        <div
          className="bg-base-300/95 border border-base-content/20 rounded-tl-lg p-2 backdrop-blur-sm"
          style={{ pointerEvents: "auto" }}
        >
          <p className="text-xs uppercase font-bold opacity-50 mb-1 text-center">
            Inventory
          </p>
          <div className="grid grid-cols-9 gap-0.5">
            {slots.slice(9, 36).map((slot, i) => (
              <button
                type="button"
                key={i + 9}
                onMouseDown={(e) => handleMouseDown(i + 9, e)}
                onMouseUp={() => handleMouseUp(i + 9)}
                className="w-10 h-10 bg-base-100 border border-base-content/30 rounded hover:border-primary hover:bg-base-200 transition-colors flex items-center justify-center relative select-none"
              >
                {slot.itemId !== null && (
                  <>
                    <span className="text-sm font-bold">{slot.itemId}</span>
                    {slot.count > 1 && (
                      <span className="absolute bottom-0 right-0 text-[10px]">
                        {slot.count}
                      </span>
                    )}
                    {slot.variation !== undefined && slot.variation > 0 && (
                      <span className="absolute top-0 right-0 text-[8px] text-accent">
                        v{slot.variation}
                      </span>
                    )}
                  </>
                )}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Hotbar */}
      <div
        className={`border-l border-t border-base-content/20 rounded-tl-lg p-1 backdrop-blur-sm transition-opacity duration-300 ${
          hotbarVisible
            ? "bg-base-300/90 opacity-100"
            : "bg-base-300/40 opacity-40"
        }`}
        style={{ pointerEvents: isOpen ? "auto" : "none" }}
      >
        <div className="grid grid-cols-3 gap-0.5">
          {[6, 7, 8, 3, 4, 5, 0, 1, 2].map((slotIndex) => {
            const slot = slots[slotIndex];
            const isHovered = hoveredSlot === slotIndex;
            const isSelected = selectedSlot === slotIndex;
            return (
              <button
                type="button"
                key={slotIndex}
                onMouseDown={(e) => handleMouseDown(slotIndex, e)}
                onMouseUp={() => handleMouseUp(slotIndex)}
                onMouseEnter={() => setHoveredSlot(slotIndex)}
                onMouseLeave={() => setHoveredSlot(null)}
                className={`w-12 h-12 border rounded transition-all flex items-center justify-center relative select-none ${
                  isSelected
                    ? "border-primary bg-primary/20 scale-105"
                    : isHovered
                    ? "border-accent bg-accent/20"
                    : "border-base-content/30 bg-base-100/80"
                }`}
              >
                {slot.itemId !== null && (
                  <>
                    <span className="text-lg font-bold">{slot.itemId}</span>
                    {slot.count > 1 && (
                      <span className="absolute bottom-0 right-0.5 text-[10px] font-mono">
                        {slot.count}
                      </span>
                    )}
                    {slot.variation !== undefined && slot.variation > 0 && (
                      <span className="absolute top-0 right-0 text-[8px] text-accent">
                        v{slot.variation}
                      </span>
                    )}
                  </>
                )}
                <span className="absolute top-0 left-0.5 text-[10px] opacity-30">
                  {slotIndex + 1}
                </span>
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
}
